import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  Loader2,
  Briefcase,
  Heart,
  Coins,
  Compass,
  Lock,
} from "lucide-react";
import { apiClient, getBirthDetails, getProfileKey, getSessionId } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const FREE_USED_KEY = "mantrana_free_chat_used";
const REMAINING_KEY = "mantrana_remaining_questions";

export default function MantranaChat() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [freeUsed, setFreeUsed] = useState(
    () => localStorage.getItem(FREE_USED_KEY) === "true"
  );
  const [remaining, setRemaining] = useState(() => {
    const n = parseInt(localStorage.getItem(REMAINING_KEY) || "0", 10);
    return Number.isFinite(n) ? n : 0;
  });
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallContent, setPaywallContent] = useState(null);
  const scrollRef = useRef(null);

  // Localized default paywall content — recomputed on lang change.
  const defaultPaywall = {
    title: t("chat.paywall.title"),
    subtitle: t("chat.paywall.subtitle"),
    benefits: [
      t("pricing.pack.item.1"),
      t("pricing.pack.item.2"),
      t("pricing.pack.item.3"),
      t("pricing.pack.item.4"),
    ],
    packs: [
      { id: "pack_1", label: t("pw.pack.1q"), questions: 1, price_inr: 299, recommended: false },
      { id: "pack_3", label: t("pw.pack.3q"), questions: 3, price_inr: 599, recommended: true, save_label: lang === "hi" ? "₹298 की बचत" : "Save ₹298" },
    ],
  };
  const activePaywall = paywallContent || defaultPaywall;

  const SUGGESTIONS = [
    { icon: Briefcase, label: t("chat.suggest.career.label"), prompt: t("chat.suggest.career.q") },
    { icon: Heart, label: t("chat.suggest.relationship.label"), prompt: t("chat.suggest.relationship.q") },
    { icon: Coins, label: t("chat.suggest.finance.label"), prompt: t("chat.suggest.finance.q") },
    { icon: Compass, label: t("chat.suggest.purpose.label"), prompt: t("chat.suggest.purpose.q") },
  ];

  const canAsk = !freeUsed || remaining > 0;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const sid = getSessionId();
        const pk = getProfileKey();
        const usageUrl = pk
          ? `/usage/${sid}?profile_key=${encodeURIComponent(pk)}`
          : `/usage/${sid}`;
        const [usageRes, histRes] = await Promise.all([
          apiClient
            .get(usageUrl, { skipPaywall: true })
            .catch(() => ({ data: { free_reading_used: false, remaining_paid_questions: 0 } })),
          apiClient.get(`/chat/${sid}`).catch(() => ({ data: [] })),
        ]);
        const used = !!(usageRes.data?.free_reading_used || usageRes.data?.free_chat_used);
        const rem = parseInt(usageRes.data?.remaining_paid_questions ?? 0, 10) || 0;
        setFreeUsed(used);
        setRemaining(rem);
        if (used) localStorage.setItem(FREE_USED_KEY, "true");
        localStorage.setItem(REMAINING_KEY, String(rem));
        const history = histRes.data || [];
        if (history.length === 0) {
          setMessages([{ role: "assistant", content: t("chat.welcome"), id: "welcome" }]);
        } else {
          const rebuilt = [...history];
          if (used && rem === 0 && !rebuilt.some((m) => m.id === "upsell")) {
            rebuilt.push({ id: "upsell", role: "system", kind: "upsell" });
          }
          setMessages(rebuilt);
        }
      } catch (e) {
        setMessages([{ role: "assistant", content: t("chat.welcome"), id: "welcome" }]);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, paywallOpen]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;

    if (!canAsk) {
      // No free credit AND zero paid balance — navigate to full paywall
      window.location.href = "/paywall";
      return;
    }

    setInput("");
    setSending(true);
    const sid = getSessionId();
    const birth = getBirthDetails();
    const pk = getProfileKey();
    const userTurn = { role: "user", content: text, id: `tmp-${Date.now()}` };
    setMessages((m) => [...m.filter((x) => x.id !== "welcome"), userTurn]);
    try {
      const res = await apiClient.post(
        "/chat",
        {
          session_id: sid,
          message: text,
          profile_key: pk || null,
          context: birth ? { birth: JSON.stringify(birth) } : null,
          language: lang,
        },
        { skipPaywall: true }
      );
      if (res.data?.locked) {
        setPaywallContent(res.data.paywall || defaultPaywall);
        setFreeUsed(true);
        setRemaining(0);
        localStorage.setItem(FREE_USED_KEY, "true");
        localStorage.setItem(REMAINING_KEY, "0");
        setPaywallOpen(true);
        setMessages((m) => m.filter((x) => x.id !== userTurn.id));
      } else if (res.data?.message) {
        const newRem = parseInt(res.data.remaining_paid_questions ?? 0, 10) || 0;
        setRemaining(newRem);
        localStorage.setItem(REMAINING_KEY, String(newRem));
        setFreeUsed(true);
        localStorage.setItem(FREE_USED_KEY, "true");
        const additions = [res.data.message];
        // Show upsell only when the user is out of paid questions too
        if (newRem === 0) {
          additions.push({ id: `upsell-${Date.now()}`, role: "system", kind: "upsell" });
        }
        setMessages((m) => [...m, ...additions]);
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: t("err.network.body"),
          id: `err-${Date.now()}`,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const isEmpty = messages.length === 1 && messages[0].id === "welcome";

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-[72px] right-6 z-[60] w-14 h-14 rounded-full flex items-center justify-center transition-all hover:scale-105 gold-glow"
        style={{ background: "#D4AF37", color: "#0B1020" }}
        data-testid="mantra-chat-toggle"
        aria-label="Open Mantrana chat"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="fixed bottom-[140px] right-6 z-[60] w-[92vw] sm:w-[440px] h-[72vh] sm:h-[600px] rounded-3xl flex flex-col overflow-hidden"
            style={{
              background:
                "linear-gradient(170deg, rgba(19,25,43,0.94) 0%, rgba(20,17,46,0.92) 60%, rgba(35,29,80,0.90) 100%)",
              border: "1px solid rgba(212,175,55,0.28)",
              boxShadow:
                "0 30px 80px rgba(0,0,0,0.55), 0 0 60px rgba(212,175,55,0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
            }}
            data-testid="mantra-chat-panel"
          >
            {/* ambient stars + nebula */}
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none opacity-70"
              style={{
                backgroundImage:
                  "radial-gradient(1px 1px at 15% 18%, rgba(255,255,255,0.6) 50%, transparent), radial-gradient(1.2px 1.2px at 82% 24%, rgba(212,175,55,0.5) 50%, transparent), radial-gradient(1px 1px at 38% 88%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(1px 1px at 90% 72%, rgba(255,255,255,0.5) 50%, transparent), radial-gradient(1px 1px at 22% 52%, rgba(255,255,255,0.4) 50%, transparent), radial-gradient(1px 1px at 65% 60%, rgba(255,255,255,0.4) 50%, transparent)",
              }}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(60% 40% at 75% 15%, rgba(99,102,241,0.25), transparent 70%), radial-gradient(50% 40% at 20% 90%, rgba(168,85,247,0.14), transparent 70%)",
              }}
            />

            <header className="relative flex items-center gap-3 p-5 border-b border-white/5">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: "rgba(212,175,55,0.18)", boxShadow: "0 0 18px rgba(212,175,55,0.25)" }}
              >
                <Sparkles className="w-4 h-4 gold-text" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-serif text-lg text-white leading-none">{t("chat.title")}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 mt-1">
                  {canAsk
                    ? freeUsed
                      ? t("chat.status.remaining", { n: remaining })
                      : t("chat.status.alwaysHere")
                    : t("chat.status.premium")}
                </div>
              </div>
            </header>

            <div
              ref={scrollRef}
              className="relative flex-1 overflow-y-auto px-5 py-5 space-y-4"
              data-testid="chat-messages"
            >
              {messages.map((m) => {
                if (m.kind === "upsell") {
                  return (
                    <UpsellCard
                      key={m.id}
                      onUpgrade={() => (window.location.href = "/paywall")}
                      t={t}
                    />
                  );
                }
                return (
                  <div
                    key={m.id}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed font-light whitespace-pre-wrap ${
                        m.role === "user"
                          ? "text-[#0B1020]"
                          : "text-slate-100 bg-white/5 border border-white/5"
                      }`}
                      style={m.role === "user" ? { background: "#D4AF37" } : {}}
                    >
                      {m.content}
                    </div>
                  </div>
                );
              })}

              {isEmpty && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="grid grid-cols-2 gap-2.5 pt-2"
                  data-testid="chat-suggestions"
                >
                  {SUGGESTIONS.map((s, i) => (
                    <motion.button
                      key={s.label}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 + i * 0.06 }}
                      whileHover={{ y: -2 }}
                      onClick={() => send(s.prompt)}
                      className="text-left rounded-2xl p-3.5 transition-colors"
                      style={{
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                      data-testid={`chat-suggestion-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center mb-2"
                        style={{ background: "rgba(212,175,55,0.12)" }}
                      >
                        <s.icon className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} />
                      </div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-1">
                        {s.label}
                      </div>
                      <div className="text-xs text-slate-100 font-light leading-snug">
                        "{s.prompt}"
                      </div>
                    </motion.button>
                  ))}
                </motion.div>
              )}

              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-4 py-3 bg-white/5 border border-white/5 text-slate-400 inline-flex items-center gap-2 text-sm">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("chat.thinking")}
                  </div>
                </div>
              )}
            </div>

            <div className="relative p-4 border-t border-white/5">
              {!canAsk ? (
                <button
                  onClick={() => (window.location.href = "/paywall")}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-medium cta-gold"
                  data-testid="chat-locked-cta"
                >
                  <Lock className="w-4 h-4" />
                  {t("chat.unlockCta")}
                </button>
              ) : (
                <div
                  className="flex items-end gap-2 rounded-2xl px-4 py-2"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder={t("chat.placeholder")}
                    className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-500 resize-none py-2 max-h-32"
                    data-testid="chat-input"
                  />
                  <button
                    onClick={() => send()}
                    disabled={!input.trim() || sending}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity disabled:opacity-40"
                    style={{ background: "#D4AF37", color: "#0B1020" }}
                    data-testid="chat-send-button"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              )}
              {canAsk && freeUsed && remaining > 0 && (
                <div className="mt-2 text-center text-[10px] tracking-[0.2em] uppercase text-slate-500" data-testid="chat-balance-hint">
                  {t("chat.balanceHint", { n: remaining, s: remaining === 1 ? "" : "s" })}
                </div>
              )}
            </div>

            {/* PAYWALL OVERLAY — covers the chat panel */}
            <AnimatePresence>
              {paywallOpen && (
                <Paywall
                  content={activePaywall}
                  onClose={() => setPaywallOpen(false)}
                  onHome={() => {
                    setPaywallOpen(false);
                    setOpen(false);
                    window.location.href = "/";
                  }}
                  t={t}
                />
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function UpsellCard({ onUpgrade, t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="rounded-2xl p-5 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, rgba(212,175,55,0.16) 0%, rgba(49,46,129,0.22) 100%)",
        border: "1px solid rgba(212,175,55,0.35)",
        boxShadow: "0 0 32px rgba(212,175,55,0.18)",
      }}
      data-testid="chat-upsell-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 gold-text" strokeWidth={1.5} />
        <span className="text-[10px] tracking-[0.25em] uppercase gold-text">
          {t("chat.upsell.eyebrow")}
        </span>
      </div>
      <p className="text-sm text-slate-100 font-light leading-relaxed">
        {t("chat.upsell.body")}
      </p>
      <button
        onClick={onUpgrade}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-medium cta-gold"
        data-testid="chat-upsell-cta"
      >
        {t("chat.upsell.cta")}
      </button>
    </motion.div>
  );
}

function Paywall({ content, onClose, onHome, t }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="absolute inset-0 z-[70] flex flex-col"
      style={{
        background:
          "linear-gradient(170deg, rgba(11,16,32,0.96) 0%, rgba(20,17,46,0.96) 60%, rgba(35,29,80,0.96) 100%)",
        backdropFilter: "blur(28px)",
        WebkitBackdropFilter: "blur(28px)",
      }}
      data-testid="chat-paywall"
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-8 h-8 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 text-slate-300"
        aria-label="Close paywall"
        data-testid="chat-paywall-close"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex-1 overflow-y-auto flex flex-col justify-center px-7 py-8">
        <motion.div
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[9px] tracking-[0.3em] uppercase mb-5"
            style={{
              background: "rgba(212,175,55,0.12)",
              border: "1px solid rgba(212,175,55,0.4)",
              color: "#FBE38E",
            }}
          >
            <Lock className="w-3 h-3" />
            {t("pw.premiumRequired")}
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-white font-light leading-snug">
            {content.title}
          </h3>
          <p className="mt-3 text-sm text-slate-300 font-light">{content.subtitle}</p>

          <div className="mt-6 space-y-3">
            {(content.packs || []).map((pack) => (
              <div
                key={pack.id}
                className="rounded-2xl p-4 flex items-center justify-between gap-4"
                style={{
                  background: pack.recommended
                    ? "linear-gradient(140deg, rgba(212,175,55,0.16), rgba(49,46,129,0.22))"
                    : "rgba(255,255,255,0.04)",
                  border: pack.recommended
                    ? "1px solid rgba(212,175,55,0.4)"
                    : "1px solid rgba(255,255,255,0.08)",
                }}
                data-testid={`chat-paywall-pack-${pack.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-2xl text-white">₹{pack.price_inr}</span>
                    {pack.save_label && (
                      <span className="text-[9px] tracking-[0.25em] uppercase gold-text">
                        {pack.save_label}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-300 font-light mt-0.5 truncate">
                    {pack.label}
                  </div>
                </div>
                <button
                  onClick={() => (window.location.href = "/paywall")}
                  className={`rounded-full px-4 py-2 text-xs font-medium whitespace-nowrap ${
                    pack.recommended ? "cta-gold" : "border border-white/20 hover:bg-white/5 text-white"
                  }`}
                  data-testid={`chat-paywall-buy-${pack.id}`}
                >
                  {pack.questions === 1
                    ? t("pricing.pack.getOne", { price: pack.price_inr })
                    : t("pricing.pack.getN", { n: pack.questions, price: pack.price_inr })}
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={onHome}
            className="mt-5 w-full rounded-full py-3 text-sm font-medium border border-white/15 hover:bg-white/5 text-white"
            data-testid="paywall-home-button"
          >
            {t("common.returnHome")}
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}
